import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import { getAuthCredentials } from "../../utils/auth";
import TitleSetter from "../../component/TitleSetter";
import SalesInventoryExport from "./SalesInventoryExport";

const fmtPct = (prev, curr) => {
  if (prev === 0) return "—";
  const diff = ((curr - prev) / prev) * 100;
  const arrow = diff > 0 ? "↑" : diff < 0 ? "↓" : "→";
  const color = diff > 0 ? "green" : diff < 0 ? "red" : "gray";
  return (
    <span style={{ color, fontWeight: "bold" }}>
      {arrow} {Math.abs(diff).toFixed(1)}%
    </span>
  );
};

const fmtPeso = (val) =>
  `₱${(val || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

export default function SalesInventory({ dateRange }) {
  const { user, token, posId } = getAuthCredentials();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuSummary, setMenuSummary] = useState([]);
  const [inventorySummary, setInventorySummary] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/sales/inventory-report`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              posId,
              startDate: dayjs(dateRange[0]).format("YYYY-MM-DDTHH:mm:ss"),
              endDate: dayjs(dateRange[1]).format("YYYY-MM-DDTHH:mm:ss"),
            },
          }
        );
        // sort menus by cost descending
        const sorted = data.menuSummary.sort((a, b) => b.cost - a.cost);
        setMenuSummary(sorted);
        setInventorySummary(data.inventorySummary);
      } catch (err) {
        console.error(err);
        setError("Failed to load inventory data");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [dateRange, posId, token]);

  if (loading)
    return (
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Typography color="error" align="center" sx={{ mt: 3 }}>
        {error}
      </Typography>
    );

  // filter & resort
  const filteredMenus = menuSummary
    .filter((m) => m.menu.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => b.cost - a.cost);

  // current selection
  const selected =
    filteredMenus.find((m) => m.menu === openMenu) || filteredMenus[0] || {};
  const { breakdown = [] } = selected;

  return (
    <Box sx={{ p: 3 }}>
      <TitleSetter title="Inventory Usage by Menu" />

      {/* Export Button */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <SalesInventoryExport dateRange={dateRange} />
      </Box>

      {/* 1) Search + Menu list */}
      <Typography variant="h6" gutterBottom>
        Menu Items
      </Typography>
      <TextField
        placeholder="Search menu..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 1 }}
      />
      <Paper variant="outlined" sx={{ maxHeight: 240, overflow: "auto" }}>
        <List disablePadding>
          {filteredMenus.length > 0 ? (
            filteredMenus.map((m) => (
              <ListItemButton
                key={m.menu}
                selected={openMenu === m.menu}
                onClick={() => setOpenMenu(m.menu)}
              >
                <ListItemText
                  primary={m.menu}
                  secondary={`Sold: ${m.sold} · Cost: ${fmtPeso(m.cost)}`}
                />
              </ListItemButton>
            ))
          ) : (
            <Typography
              variant="body2"
              align="center"
              sx={{ p: 2, color: "text.secondary" }}
            >
              No matching menu items.
            </Typography>
          )}
        </List>
      </Paper>

      {/* 2) Per-menu breakdown */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6">
          Inventory Used for “{selected.menu}”
        </Typography>
        <TableContainer component={Paper} sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ingredient</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell align="right">Qty Used</TableCell>
                <TableCell align="right">Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {breakdown.length > 0 ? (
                breakdown.map((b, i) => (
                  <TableRow key={i}>
                    <TableCell>{b.inventory}</TableCell>
                    <TableCell>{b.unit}</TableCell>
                    <TableCell align="right">{b.qty}</TableCell>
                    <TableCell align="right">{fmtPeso(b.cost)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No usage data.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* 3) Overall Inventory Consumption */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h6" gutterBottom>
          Overall Inventory Consumption
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Inventory</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell align="right">Previous Qty Used</TableCell>
                <TableCell align="right">Current Qty Used</TableCell>
                <TableCell align="right">Δ % (Qty)</TableCell>
                <TableCell align="right">Previous Cost</TableCell>
                <TableCell align="right">Current Cost</TableCell>
                <TableCell align="right">Δ % (Cost)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventorySummary.length > 0 ? (
                inventorySummary.map((inv, i) => {
                  const { previousQty: pq, currentQty: cq } = inv;
                  const { previousCost: pC, currentCost: cC } = inv;
                  return (
                    <TableRow key={i}>
                      <TableCell>{inv.name}</TableCell>
                      <TableCell>{inv.unit}</TableCell>
                      <TableCell align="right">{pq}</TableCell>
                      <TableCell align="right">{cq}</TableCell>
                      <TableCell align="right">{fmtPct(pq, cq)}</TableCell>
                      <TableCell align="right">{fmtPeso(pC)}</TableCell>
                      <TableCell align="right">{fmtPeso(cC)}</TableCell>
                      <TableCell align="right">{fmtPct(pC, cC)}</TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No inventory data available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
